import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useOutletContext } from 'react-router-dom';
import WeekNavigator from '../report/WeekNavigator';
import AttendanceDetailPanel from './AttendanceDetailPanel';
import { GridLockToggle, UnlockPromptModal, useLockedClickGuard } from './GridLock';
import ConfirmationModal from '../ConfirmationModal';
import { ChildProfile } from '../../models/childProfile';
import { ScheduleDataService } from '../../services/scheduleDataService';
import { AttendanceDataService, AttendanceRecord } from '../../services/attendanceDataService';
import {
  NO_SHOW_REASON_LABELS,
  composeNoShowComment,
} from '../../services/attendanceReasons';
import { ScheduleSession } from './SessionDetailPanel';
import { LayoutOutletContext } from '../Layout';
import { useAppState } from '../../context/AppStateContext';
import { ORG_CONFIG_DEFAULTS } from '../../models/orgConfig';
import { toDateString, getMonday, formatDisplayDate } from '../../services/dateUtils';
import { DAY_OFFSETS, occursOnDate } from './scheduleOccurrences';
import { timeToMins, buildSlots } from './scheduleTime';
import './schedule.scss';

interface WeeklyAttendanceProps {
  childProfiles: ChildProfile[];
  weekStart: string | null;
  onWeekChange: (monday: string, friday: string) => void;
  childIdFilter: string | null;
  teacherFilterId?: number | null;
}

const WEEKDAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'];
const WEEKEND = ['Saturday', 'Sunday'];

const SESSION_HEIGHT = 30;
const MIN_LANES = 2;
const DETAIL_PANEL_WIDTH = 300;


// ── Quick-action menu types ────────────────────────────────

// Use the shared canonical list so the inline picker offers the same
// options as the Attendance detail editor and the dashboard's confirm
// modal. Reason is stored as the label string directly.
type NoShowReasonInline = string;
const NO_SHOW_PRESETS_INLINE: ReadonlyArray<string> = NO_SHOW_REASON_LABELS;
const DEFAULT_INLINE_REASON: NoShowReasonInline = NO_SHOW_REASON_LABELS[0];
const composeNoShowCommentInline = composeNoShowComment;

// ── Status filter chips ──────────────────────────────────────

type StatusChip = 'all' | 'attended' | 'no-show' | 'late' | 'pending';
const CHIP_LABELS: Record<StatusChip, string> = {
  all: 'All',
  attended: 'Attended',
  'no-show': 'No-show',
  late: 'Late',
  pending: 'Pending',
};

// ── Component ────────────────────────────────────────────────

const WeeklyAttendance: React.FC<WeeklyAttendanceProps> = ({
  childProfiles,
  weekStart,
  onWeekChange,
  childIdFilter,
  teacherFilterId = null,
}) => {
  // Collapse the left filters panel when the detail panel slides in (matches
  // the schedule view's behaviour).
  const { setSidebarOpen } = useOutletContext<LayoutOutletContext>();

  // Hydration state — same shape as WeeklySchedule
  const [sessions, setSessions] = useState<ScheduleSession[]>([]);
  const [cancelledExceptionDates, setCancelledExceptionDates] = useState<
    Map<string, Set<string>>
  >(new Map()); // key: localSessionId -> Set<'YYYY-MM-DD'>
  const [attendance, setAttendance] = useState<AttendanceRecord[]>([]);

  // ── Grid lock ──────────────────────────────────────────────
  // Locked by default. Clicking an existing attendance record (solid bar)
  // always opens the side panel. Clicking an empty ghost session bar (or
  // its "..." quick menu) is gated by the lock; the prompt modal offers to
  // unlock.
  const [gridLocked, setGridLocked] = useState<boolean>(true);
  const [unlockPromptOpen, setUnlockPromptOpen] = useState<boolean>(false);
  const { flashKey: lockFlashKey, guard: lockedClickGuard } =
    useLockedClickGuard(() => setUnlockPromptOpen(true));

  // Right-hand panel
  const [panelOpen, setPanelOpen] = useState(false);
  const [panelContext, setPanelContext] = useState<{
    sessionLocalId: string;
    scheduleItemServerId: number;
    childId: number;
    date: string;
    scheduledStart: string;
    scheduledEnd: string;
  } | null>(null);

  // Status filter chips
  const [statusChip, setStatusChip] = useState<StatusChip>('all');

  // Hover state for day-row coloring + tooltips on overlay variance
  const [hoveredCell, setHoveredCell] = useState<string | null>(null);
  // Hover state for ghost-bar quick-action affordance. Key is `${sessionLocalId}-${dateStr}`.
  const [hoveredGhostKey, setHoveredGhostKey] = useState<string | null>(null);
  // Which ghost has its quick-action dropdown open (mutually exclusive: at
  // most one). Outside-click closes. The anchor rect is captured from the
  // trigger button at open-time and used to position the portal'd dropdown.
  const [openMenuKey, setOpenMenuKey] = useState<string | null>(null);
  const [menuAnchorRect, setMenuAnchorRect] = useState<DOMRect | null>(null);
  const [menuTarget, setMenuTarget] = useState<{ session: ScheduleSession; dateStr: string } | null>(null);
  // Per-menu no-show reason state (transient, scoped to the open menu).
  const [menuNoShowReason, setMenuNoShowReason] = useState<NoShowReasonInline>(DEFAULT_INLINE_REASON);
  const [menuNoShowFreeText, setMenuNoShowFreeText] = useState<string>('');

  // Confirmation modal (for unsaved-discard prompts)
  interface ConfirmConfig {
    title: string;
    message: string;
    confirmText?: string;
    isDeleting?: boolean;
  }
  const [confirmConfig, setConfirmConfig] = useState<ConfirmConfig | null>(null);
  const confirmResolveRef = useRef<((ok: boolean) => void) | null>(null);
  const confirmAction = (config: ConfirmConfig): Promise<boolean> => {
    setConfirmConfig(config);
    return new Promise<boolean>(resolve => {
      confirmResolveRef.current = resolve;
    });
  };

  // ── Derived ──────────────────────────────────────────────

  // Per-org grid configuration. Null orgConfig (still loading or no org
  // selected) → fall back to the safe defaults exported by orgConfig.ts,
  // which match migration 014's column defaults.
  const { orgConfig } = useAppState();
  const cfg = orgConfig ?? ORG_CONFIG_DEFAULTS;
  const slotMin = cfg.defaultSessionIntervalMinutes;

  // Grid spans the working day exactly, matching the Weekly Schedule view so
  // the two grids line up. Early arrivals / late departures are clamped to
  // this window by barPositionPercent rather than widening the grid.
  const gridStart = cfg.workingDayStart;
  const gridEnd = cfg.workingDayEnd;

  const days = cfg.includeWeekends ? [...WEEKDAYS, ...WEEKEND] : WEEKDAYS;
  const slots = buildSlots(slotMin, gridStart, gridEnd);
  const selectedChildIdNum = childIdFilter ? Number(childIdFilter) : null;

  // ── "Now" indicator ────────────────────────────────────────
  const [nowDate, setNowDate] = useState<Date>(new Date());
  useEffect(() => {
    const id = setInterval(() => setNowDate(new Date()), 60_000);
    return () => clearInterval(id);
  }, []);
  const todayDateStr = toDateString(nowDate);

  // Percent across the lanes track for the current moment, or null when
  // outside the displayed grid window (gridStart → gridEnd).
  const nowPercentInDay: number | null = (() => {
    const [sh, sm] = gridStart.split(':').map(Number);
    const [eh, em] = gridEnd.split(':').map(Number);
    const startMins = sh * 60 + sm;
    const endMins = eh * 60 + em;
    const total = endMins - startMins;
    if (total <= 0) return null;
    const nowMins = nowDate.getHours() * 60 + nowDate.getMinutes() + nowDate.getSeconds() / 60;
    if (nowMins < startMins || nowMins > endMins) return null;
    return ((nowMins - startMins) / total) * 100;
  })();

  // ── Hydration ───────────────────────────────────────────

  // Schedules + exceptions
  const sessionServerIdToLocal = useRef<Map<number, string>>(new Map());
  const nextLocalId = useRef(1);

  // Auto-save plumbing — mirrors the WeeklySchedule fresh-mode race handling.
  // Keyed by `${scheduleItemServerId}-${childId}-${date}` to identify the
  // attendance record across its create + immediate-edit lifecycle.
  const attendanceKey = (scheduleItemServerId: number, childId: number, date: string): string =>
    `${scheduleItemServerId}-${childId}-${date}`;
  const createPromisesRef = useRef<Map<string, Promise<{ id: number }>>>(new Map());
  const attendanceServerIdsRef = useRef<Map<string, number>>(new Map());

  // Fresh-mode set: records the user just clicked-into for the first time
  // (auto-create flow). While fresh, edits live-PATCH. The key leaves the
  // set when the panel closes or selection changes — re-opening later puts
  // the panel into "settled" mode, requiring an explicit Save click.
  const [freshAttendanceKeys, setFreshAttendanceKeys] = useState<Set<string>>(new Set());

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [items, exceptions] = await Promise.all([
          ScheduleDataService.listScheduleItems(),
          ScheduleDataService.listScheduleExceptions(),
        ]);
        if (cancelled) return;

        const localIdByServer = new Map<number, string>();
        const newSessions: ScheduleSession[] = items.map(it => {
          const localId = `att-session-${nextLocalId.current++}`;
          localIdByServer.set(it.serverId, localId);
          return {
            id: localId,
            lane: 0,
            dayOfWeek: it.dayOfWeek,
            startTime: it.startTime,
            endTime: it.endTime,
            childId: it.childId,
            teacherIds: [...it.teacherIds],
            locationIds: [...it.locationIds],
            frequency: it.frequency,
            startDate: it.startDate,
            endDate: it.endDate,
            serverId: it.serverId,
          };
        });

        sessionServerIdToLocal.current = localIdByServer;

        // Track cancellation exceptions per (localId, date) for projection skipping.
        const cancelMap = new Map<string, Set<string>>();
        for (const ex of exceptions) {
          if (ex.status !== 'cancelled') continue;
          const localId = localIdByServer.get(ex.scheduleItemServerId);
          if (!localId) continue;
          const dates = cancelMap.get(localId) ?? new Set<string>();
          dates.add(ex.date);
          cancelMap.set(localId, dates);
        }

        setSessions(newSessions);
        setCancelledExceptionDates(cancelMap);
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error('Failed to hydrate schedules for attendance', err);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Sidebar collapse driven by panel state. One-way: opening the panel takes
  // the space, closing it doesn't hand it back, and arriving with no panel
  // open changes nothing — only the burger reopens the menu.
  useEffect(() => {
    if (panelOpen) setSidebarOpen(false);
  }, [panelOpen, setSidebarOpen]);

  // Attendance — re-fetches when the viewed week changes.
  useEffect(() => {
    if (!weekStart) return;
    let cancelled = false;
    const monday = new Date(weekStart + 'T00:00');
    const sunday = new Date(monday);
    sunday.setDate(sunday.getDate() + 6);
    const from = toDateString(monday);
    const to = toDateString(sunday);
    AttendanceDataService.listAttendance(from, to)
      .then(rows => { if (!cancelled) setAttendance(rows); })
      .catch(err => {
        // eslint-disable-next-line no-console
        console.error('Failed to load attendance', err);
      });
    return () => { cancelled = true; };
  }, [weekStart]);

  // ── Helpers ──────────────────────────────────────────────

  const getDateForWeekday = (dayName: string): string => {
    const base = weekStart ? new Date(weekStart + 'T00:00') : getMonday(new Date());
    base.setDate(base.getDate() + (DAY_OFFSETS[dayName] ?? 0));
    return toDateString(base);
  };

  // The shared occurrence rule, plus this page's own: a cancelled date is
  // hidden here rather than drawn greyed out.
  const isSessionActiveOnDate = (s: ScheduleSession, dateStr: string): boolean => {
    if (cancelledExceptionDates.get(s.id)?.has(dateStr)) return false;
    return occursOnDate(s, new Date(dateStr + 'T00:00'));
  };

  // Whitelist-aware set of child IDs the current user is allowed to see.
  // Home.fetchChildProfiles has already filtered `childProfiles` to the
  // user's whitelist (when one applies), so deriving from it gives us the
  // pre-filter we want for *all* attendance data. Admins / unrestricted
  // users get the full org list, so this is a no-op for them.
  const allowedChildIds = useMemo(
    () => new Set(childProfiles.map(c => c.id)),
    [childProfiles],
  );

  const matchesSessionFilters = (s: ScheduleSession): boolean => {
    if (s.childId == null || !allowedChildIds.has(s.childId)) return false;
    if (selectedChildIdNum !== null && s.childId !== selectedChildIdNum) return false;
    if (teacherFilterId !== null && !s.teacherIds.includes(teacherFilterId)) return false;
    return true;
  };

  // Find the attendance row (if any) for a given (session, date).
  const getAttendance = (s: ScheduleSession, dateStr: string): AttendanceRecord | null => {
    if (s.serverId === undefined || s.childId == null) return null;
    return (
      attendance.find(
        a =>
          a.scheduleItemServerId === s.serverId &&
          a.childId === s.childId &&
          a.attendanceDate === dateStr,
      ) ?? null
    );
  };

  // Compute the displayed status for a given (session, date).
  type DisplayStatus = 'attended' | 'no-show' | 'late' | 'pending';
  const computeStatus = (
    s: ScheduleSession,
    dateStr: string,
  ): DisplayStatus | null => {
    const todayStr = toDateString(new Date());
    const inFuture = dateStr > todayStr;
    const att = getAttendance(s, dateStr);
    if (att) {
      if (att.actualArrival === null && att.actualDeparture === null) return 'no-show';
      if (att.actualArrival && att.actualArrival > s.startTime) return 'late';
      return 'attended';
    }
    if (inFuture) return null; // future ghost — not really 'pending'
    return 'pending';
  };

  const passesStatusChip = (status: DisplayStatus | null): boolean => {
    if (statusChip === 'all') return true;
    if (status === null) return false; // future ghosts only show under "All"
    return status === statusChip;
  };

  // Calendar date for each weekday (used to show on day labels).
  const dayMeta = useMemo(() => {
    if (!weekStart) return {} as Record<string, { display: string }>;
    const base = new Date(weekStart + 'T00:00');
    const names = [...WEEKDAYS, ...WEEKEND];
    const out: Record<string, { display: string }> = {};
    for (const n of names) {
      const d = new Date(base);
      d.setDate(d.getDate() + (DAY_OFFSETS[n] ?? 0));
      // dd MMM (year omitted in the grid — already in the week navigator).
      out[n] = { display: formatDisplayDate(d).replace(/\s\d{4}$/, '') };
    }
    return out;
  }, [weekStart]);

  // ── Click handler — open panel ────────────────────────────

  // Shared open-panel-with-optional-auto-create flow. The `initial` payload
  // determines whether the auto-created record is "Attended at scheduled
  // times" (default click) or a no-show. The `openPanel` flag controls
  // whether the right-hand editor slides in afterward — `true` for clicks
  // on the bar (so the user can tweak), `false` for the inline dropdown
  // menu (where the user has already declared their intent).
  const startAttendanceFlow = (
    s: ScheduleSession,
    dateStr: string,
    initial: {
      actualArrival: string | null;
      actualDeparture: string | null;
      arrivalComment: string | null;
    },
    openPanel: boolean = true,
  ) => {
    if (s.serverId === undefined || s.childId == null) return; // can't log against an unsaved rule
    const todayStr = toDateString(new Date());
    if (dateStr > todayStr) {
      // Future date — no-op for v1 (toast layer not yet wired).
      return;
    }

    const scheduleItemServerId = s.serverId;
    const childId = s.childId;
    const key = attendanceKey(scheduleItemServerId, childId, dateStr);
    const existing = attendance.find(
      a =>
        a.scheduleItemServerId === scheduleItemServerId &&
        a.childId === childId &&
        a.attendanceDate === dateStr,
    );

    if (openPanel) {
      setPanelContext({
        sessionLocalId: s.id,
        scheduleItemServerId,
        childId,
        date: dateStr,
        scheduledStart: s.startTime,
        scheduledEnd: s.endTime,
      });
      setPanelOpen(true);
    }

    // Fresh mode is for newly-created records only. Re-opening an existing
    // log puts the panel in "settled" mode — edits buffer until the user
    // clicks Save changes. Matches the schedule view's pattern.
    if (!existing) {
      setFreshAttendanceKeys(prev => {
        const next = new Set(prev);
        next.add(key);
        return next;
      });
    } else {
      setFreshAttendanceKeys(prev => {
        if (!prev.has(key)) return prev;
        const next = new Set(prev);
        next.delete(key);
        return next;
      });
      return; // existing record — no auto-create needed
    }

    // Optimistic insert.
    const optimistic: AttendanceRecord = {
      scheduleItemServerId,
      childId,
      attendanceDate: dateStr,
      ...initial,
      departureComment: null,
    };
    setAttendance(prev => [...prev, optimistic]);

    const promise = AttendanceDataService.createAttendance({
      scheduleItemServerId,
      childId,
      attendanceDate: dateStr,
      ...initial,
      departureComment: null,
    });
    createPromisesRef.current.set(key, promise);
    promise
      .then(({ id }) => {
        attendanceServerIdsRef.current.set(key, id);
        createPromisesRef.current.delete(key);
        setAttendance(prev =>
          prev.map(a =>
            a.scheduleItemServerId === scheduleItemServerId &&
            a.childId === childId &&
            a.attendanceDate === dateStr &&
            a.serverId === undefined
              ? { ...a, serverId: id, id }
              : a,
          ),
        );
      })
      .catch(err => {
        createPromisesRef.current.delete(key);
        setAttendance(prev =>
          prev.filter(
            a =>
              !(
                a.scheduleItemServerId === scheduleItemServerId &&
                a.childId === childId &&
                a.attendanceDate === dateStr &&
                a.serverId === undefined
              ),
          ),
        );
        // eslint-disable-next-line no-console
        console.error('Failed to auto-create attendance log', err);
      });
  };

  const handleSessionClick = (s: ScheduleSession, dateStr: string) =>
    startAttendanceFlow(s, dateStr, {
      actualArrival: s.startTime,
      actualDeparture: s.endTime,
      arrivalComment: null,
    });

  // Menu actions — commit the record AND open the right-hand panel for it
  // (user expects "selecting a menu option" to also select the record on
  // the right, so they can review or fine-tune in one motion).
  const handleMenuMarkAttended = (s: ScheduleSession, dateStr: string) => {
    startAttendanceFlow(
      s,
      dateStr,
      { actualArrival: s.startTime, actualDeparture: s.endTime, arrivalComment: null },
      true,
    );
    closeQuickMenu();
  };

  const handleMenuMarkNoShow = (s: ScheduleSession, dateStr: string) => {
    // Validation gate: "Other" reason requires free text. Held until supplied.
    if (menuNoShowReason === 'Other' && menuNoShowFreeText.trim().length === 0) return;
    const comment = composeNoShowCommentInline(menuNoShowReason, menuNoShowFreeText);
    startAttendanceFlow(
      s,
      dateStr,
      { actualArrival: null, actualDeparture: null, arrivalComment: comment },
      true,
    );
    closeQuickMenu();
  };

  const closeQuickMenu = () => {
    setOpenMenuKey(null);
    setMenuAnchorRect(null);
    setMenuTarget(null);
    setMenuNoShowReason(DEFAULT_INLINE_REASON);
    setMenuNoShowFreeText('');
  };

  // Outside-click + Escape close. Also closes on any scroll, since the
  // portal'd dropdown is anchored to the trigger's pre-scroll coordinates
  // and would otherwise drift visually away from its bar.
  useEffect(() => {
    if (!openMenuKey) return;
    const onMouseDown = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      // The menu's trigger has class .wsg-attendance-quickmenu-trigger; the
      // portal'd popup has class .wsg-attendance-quickmenu-popup. Either
      // counts as "inside" the menu for click-out detection.
      if (
        !target.closest('.wsg-attendance-quickmenu-trigger') &&
        !target.closest('.wsg-attendance-quickmenu-popup')
      ) {
        closeQuickMenu();
      }
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') closeQuickMenu();
    };
    const onScroll = () => closeQuickMenu();
    document.addEventListener('mousedown', onMouseDown);
    document.addEventListener('keydown', onKey);
    // Capture-phase scroll: catches scrolls in any nested container.
    document.addEventListener('scroll', onScroll, true);
    window.addEventListener('resize', onScroll);
    return () => {
      document.removeEventListener('mousedown', onMouseDown);
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('resize', onScroll);
    };
  }, [openMenuKey]);

  // Live-update callback wired into the panel. Each call PATCHes the record
  // with the supplied diff. While the initial create is in flight, the PATCH
  // is queued on the create promise so we never duplicate-create.
  const handleAttendanceLiveUpdate = (
    updates: {
      actualArrival?: string | null;
      actualDeparture?: string | null;
      arrivalComment?: string | null;
      departureComment?: string | null;
    },
  ) => {
    if (!panelContext) return;
    const { scheduleItemServerId, childId, date } = panelContext;
    const key = attendanceKey(scheduleItemServerId, childId, date);

    // Apply locally so the panel + grid update immediately.
    setAttendance(prev =>
      prev.map(a =>
        a.scheduleItemServerId === scheduleItemServerId &&
        a.childId === childId &&
        a.attendanceDate === date
          ? { ...a, ...updates }
          : a,
      ),
    );

    const refServerId = attendanceServerIdsRef.current.get(key);
    if (refServerId !== undefined) {
      // Already persisted → live-PATCH.
      AttendanceDataService.updateAttendance(refServerId, date, updates).catch(err => {
        // eslint-disable-next-line no-console
        console.error('Failed to live-PATCH attendance', err);
      });
      return;
    }

    const inFlight = createPromisesRef.current.get(key);
    if (inFlight) {
      // Create in flight — chain a PATCH for once it resolves.
      inFlight
        .then(({ id }) => AttendanceDataService.updateAttendance(id, date, updates))
        .catch(err => {
          // eslint-disable-next-line no-console
          console.error('Failed to PATCH attendance after pending create', err);
        });
    }
  };

  const handlePanelDeleted = async () => {
    if (!panelContext) return;
    const { scheduleItemServerId, childId, date } = panelContext;
    const key = attendanceKey(scheduleItemServerId, childId, date);
    const refServerId = attendanceServerIdsRef.current.get(key);
    const local = attendance.find(
      a =>
        a.scheduleItemServerId === scheduleItemServerId &&
        a.childId === childId &&
        a.attendanceDate === date,
    );
    const serverId = local?.serverId ?? refServerId;

    // Drop the local row immediately; reverts the bar to a ghost.
    setAttendance(prev =>
      prev.filter(
        a =>
          !(
            a.scheduleItemServerId === scheduleItemServerId &&
            a.childId === childId &&
            a.attendanceDate === date
          ),
      ),
    );
    attendanceServerIdsRef.current.delete(key);

    if (serverId !== undefined) {
      try {
        await AttendanceDataService.deleteAttendance(serverId);
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error('Failed to delete attendance', err);
      }
    }

    setPanelOpen(false);
    setPanelContext(null);
  };

  const handlePanelClose = () => {
    if (panelContext) {
      const key = attendanceKey(
        panelContext.scheduleItemServerId,
        panelContext.childId,
        panelContext.date,
      );
      setFreshAttendanceKeys(prev => {
        if (!prev.has(key)) return prev;
        const next = new Set(prev);
        next.delete(key);
        return next;
      });
    }
    setPanelOpen(false);
    setPanelContext(null);
  };

  // Explicit save for settled-mode panel edits — applies the buffered diff.
  const handlePanelSave = (
    diff: {
      actualArrival?: string | null;
      actualDeparture?: string | null;
      arrivalComment?: string | null;
      departureComment?: string | null;
    },
  ) => {
    if (!panelContext) return;
    const { scheduleItemServerId, childId, date } = panelContext;
    const key = attendanceKey(scheduleItemServerId, childId, date);
    const refServerId = attendanceServerIdsRef.current.get(key);
    const local = attendance.find(
      a =>
        a.scheduleItemServerId === scheduleItemServerId &&
        a.childId === childId &&
        a.attendanceDate === date,
    );
    const serverId = local?.serverId ?? refServerId;
    if (serverId === undefined) return; // shouldn't happen — settled implies persisted

    // Apply optimistically.
    setAttendance(prev =>
      prev.map(a =>
        a.scheduleItemServerId === scheduleItemServerId &&
        a.childId === childId &&
        a.attendanceDate === date
          ? { ...a, ...diff }
          : a,
      ),
    );
    AttendanceDataService.updateAttendance(serverId, date, diff).catch(err => {
      // eslint-disable-next-line no-console
      console.error('Failed to save attendance edit', err);
    });
  };

  // Existing log for the panel (if any).
  const existingForPanel: AttendanceRecord | null = panelContext
    ? attendance.find(
        a =>
          a.scheduleItemServerId === panelContext.scheduleItemServerId &&
          a.childId === panelContext.childId &&
          a.attendanceDate === panelContext.date,
      ) ?? null
    : null;

  const isPanelFresh: boolean = panelContext
    ? freshAttendanceKeys.has(
        attendanceKey(
          panelContext.scheduleItemServerId,
          panelContext.childId,
          panelContext.date,
        ),
      )
    : false;

  // ── Hovered column index for time bars ─────────────────────
  const hoveredIdx = (() => {
    if (!hoveredCell) return -1;
    const last = hoveredCell.lastIndexOf(':');
    return Number(hoveredCell.substring(last + 1));
  })();

  // ── Time bar (top + bottom) ──────────────────────────────

  const timeBar = (position: 'top' | 'bottom') => (
    <div className={`wsg-timebar wsg-timebar--${position}`}>
      <div className="wsg-timebar__gutter" />
      <div className="wsg-timebar__track">
        {slots.map((slot, idx) => {
          const isColHovered = idx === hoveredIdx;
          return (
            <div
              key={idx}
              className={`wsg-timebar__cell${isColHovered ? ' is-hovered' : ''}`}
            >
              {isColHovered ? (
                <span className="wsg-timebar__range">
                  {slot.start}&ndash;{slot.end}
                </span>
              ) : slot.hourStart ? (
                <span className="wsg-timebar__hour">{slot.start}</span>
              ) : null}
            </div>
          );
        })}
      </div>
    </div>
  );

  // ── Per-day session list (visible sessions for that day this week) ──

  const visibleSessionsForDay = (day: string): ScheduleSession[] => {
    const dateStr = getDateForWeekday(day);
    return sessions.filter(s => {
      if (s.dayOfWeek !== day) return false;
      if (!matchesSessionFilters(s)) return false;
      if (!isSessionActiveOnDate(s, dateStr)) return false;
      const status = computeStatus(s, dateStr);
      if (!passesStatusChip(status)) return false;
      return true;
    });
  };

  // ── Bar rendering helpers ────────────────────────────────

  // Position a bar within the day track using the slot grid as a reference.
  // Returns left/width as percentages.
  const barPositionPercent = (start: string, end: string) => {
    const trackStart = timeToMins(slots[0].start);
    const trackEnd = timeToMins(slots[slots.length - 1].end);
    const total = trackEnd - trackStart;
    const sMins = Math.max(timeToMins(start), trackStart);
    const eMins = Math.min(timeToMins(end), trackEnd);
    const left = ((sMins - trackStart) / total) * 100;
    const width = ((eMins - sMins) / total) * 100;
    return { left, width };
  };

  const getColour = (s: ScheduleSession): string => {
    if (s.childId == null) return '#aaaaaa';
    return childProfiles.find(c => c.id === s.childId)?.colour_code || '#aaaaaa';
  };

  const getInitials = (s: ScheduleSession): string => {
    if (s.childId == null) return '';
    return childProfiles.find(c => c.id === s.childId)?.initials ?? '';
  };

  // Lane assignment for the day (avoid overlap of ghost bars).
  const assignLanes = (daySessions: ScheduleSession[]): Map<string, number> => {
    const sorted = [...daySessions].sort(
      (a, b) => timeToMins(a.startTime) - timeToMins(b.startTime),
    );
    const lanes: Array<{ end: number }[]> = [];
    const map = new Map<string, number>();
    for (const s of sorted) {
      const sStart = timeToMins(s.startTime);
      const sEnd = timeToMins(s.endTime);
      let lane = 0;
      while (true) {
        const cur = lanes[lane] ?? [];
        const overlap = cur.some(r => sStart < r.end);
        if (!overlap) break;
        lane++;
      }
      const cur = lanes[lane] ?? [];
      cur.push({ end: sEnd });
      lanes[lane] = cur;
      map.set(s.id, lane);
    }
    return map;
  };

  // ── Render ─────────────────────────────────────────────────

  return (
    <div style={{ display: 'flex', width: '100%', minHeight: 0 }}>
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
        {weekStart && (
          <WeekNavigator weekStart={weekStart} onWeekChange={onWeekChange} allowFuture />
        )}
        <div className="wsg-root" style={{ flex: 1, overflow: 'auto' }}>
          <div className="wsg-card">
            {/* Header */}
            <div className="wsg-header">
              <div>
                <h4 className="wsg-heading">Attendance</h4>
              </div>
              <div className="wsg-config">
                <span data-help="attendance.grid-lock" style={{ display: 'inline-flex' }}>
                <GridLockToggle
                  locked={gridLocked}
                  onToggle={() => setGridLocked(v => !v)}
                  flashKey={lockFlashKey}
                />
                </span>
                {/* Status chips. Wrapped so help can ring the set: "Pending"
                    is what is left to record today. */}
                <span data-help="attendance.status-chips" style={{ display: 'inline-flex', gap: 6 }}>
                {(['all', 'attended', 'no-show', 'late', 'pending'] as StatusChip[]).map(chip => (
                  <button
                    key={chip}
                    type="button"
                    onClick={() => setStatusChip(chip)}
                    style={{
                      padding: '0.25rem 0.6rem',
                      borderRadius: 999,
                      border: '1px solid #26923f40',
                      background: statusChip === chip ? '#26923f' : 'transparent',
                      color: statusChip === chip ? '#fff' : '#0d4d1a',
                      fontSize: '0.78rem',
                      fontWeight: 600,
                      cursor: 'pointer',
                    }}
                  >
                    {CHIP_LABELS[chip]}
                  </button>
                ))}
                </span>
              </div>
            </div>

            {/* Grid */}
            <div
              className="wsg-grid"
              data-help="attendance.grid"
              style={{ ['--wsg-slot-count' as string]: slots.length } as React.CSSProperties}
              onMouseLeave={() => setHoveredCell(null)}
            >
              {timeBar('top')}

              {days.map((day) => {
                const visible = visibleSessionsForDay(day);
                const dateStr = getDateForWeekday(day);
                const laneMap = assignLanes(visible);
                const laneCount = Math.max(MIN_LANES, ...Array.from(laneMap.values()).map(v => v + 1), MIN_LANES);
                // Match the schedule view's lane spacing so bars line up:
                // .wsg-lanes has `padding: 2px 0` and `gap: 3px` between lanes.
                const LANE_GAP = 3;
                const LANE_PAD = 2;
                const rowHeight =
                  laneCount * SESSION_HEIGHT +
                  (laneCount - 1) * LANE_GAP +
                  LANE_PAD * 2;
                const dm = dayMeta[day];

                const isToday = dateStr === todayDateStr;
                return (
                  <div
                    key={day}
                    className={`wsg-day-row ${isToday ? 'is-today' : ''}`}
                  >
                    <div className="wsg-day-label">
                      <span className="wsg-day-name">{day}</span>
                      {dm && <span className="wsg-day-date">{dm.display}</span>}
                      {isToday && <span className="wsg-day-today">Today</span>}
                    </div>

                    {/* Track — bars positioned absolutely. Uses .wsg-lanes for
                        the slot-boundary tick background, then overlays bars. */}
                    <div
                      className="wsg-lanes"
                      style={{ position: 'relative', height: rowHeight }}
                    >
                      {isToday && nowPercentInDay !== null && (
                        <div
                          className="wsg-now-line"
                          style={{ left: `${nowPercentInDay}%` }}
                          aria-hidden
                          title={`Now · ${String(nowDate.getHours()).padStart(2, '0')}:${String(nowDate.getMinutes()).padStart(2, '0')}`}
                        />
                      )}
                      {visible.map(s => {
                          const lane = laneMap.get(s.id) ?? 0;
                          const colour = getColour(s);
                          const initials = getInitials(s);
                          const ghost = barPositionPercent(s.startTime, s.endTime);
                          const att = getAttendance(s, dateStr);
                          const status = computeStatus(s, dateStr);
                          const isNoShow = status === 'no-show';
                          const isAttended = status === 'attended' || status === 'late';
                          const top = LANE_PAD + lane * (SESSION_HEIGHT + LANE_GAP);

                          // Variance hint (hover tooltip)
                          let variance = '';
                          if (att && att.actualArrival && att.actualArrival !== s.startTime) {
                            const deltaMin = timeToMins(att.actualArrival) - timeToMins(s.startTime);
                            variance = `${deltaMin > 0 ? '+' : ''}${deltaMin} min ${
                              deltaMin > 0 ? 'late' : 'early'
                            } arrival`;
                          }
                          if (att && att.actualDeparture && att.actualDeparture !== s.endTime) {
                            const deltaMin = timeToMins(att.actualDeparture) - timeToMins(s.endTime);
                            variance += `${variance ? ', ' : ''}${deltaMin > 0 ? '+' : ''}${deltaMin} min ${
                              deltaMin > 0 ? 'late' : 'early'
                            } departure`;
                          }

                          const ghostKey = `${s.id}-${dateStr}`;
                          const isHovered = hoveredGhostKey === ghostKey;
                          const isMenuOpen = openMenuKey === ghostKey;
                          const isSelected = !!panelContext &&
                            panelContext.scheduleItemServerId === s.serverId &&
                            panelContext.childId === s.childId &&
                            panelContext.date === dateStr;
                          // The "..." trigger appears only on a true ghost (no record
                          // yet). Visible on hover OR while the menu is open so the
                          // dropdown anchor stays put as the cursor moves into it.
                          const showQuickActions = (isHovered || isMenuOpen) && !att && status === 'pending' && !gridLocked;
                          return (
                            <React.Fragment key={s.id}>
                              {/* Ghost bar — scheduled placeholder */}
                              <div
                                onMouseDown={(e) => {
                                  // Locked grid: a stray click on an empty
                                  // ghost flashes the lock toggle; only an
                                  // intentional gesture (double-click, two
                                  // clicks within 3s, or click+drag) surfaces
                                  // the unlock modal.
                                  if (gridLocked && !att) {
                                    e.stopPropagation();
                                    lockedClickGuard(e);
                                  }
                                }}
                                onClick={() => {
                                  // The mousedown guard already swallowed the
                                  // gesture for locked + ghost. Skip the click
                                  // entirely in that case.
                                  if (gridLocked && !att) return;
                                  handleSessionClick(s, dateStr);
                                }}
                                onMouseEnter={() => setHoveredGhostKey(ghostKey)}
                                onMouseLeave={() => setHoveredGhostKey(prev => prev === ghostKey ? null : prev)}
                                title={`${initials} · scheduled ${s.startTime}–${s.endTime}${
                                  variance ? ` · ${variance}` : ''
                                }`}
                                style={{
                                  position: 'absolute',
                                  left: `${ghost.left}%`,
                                  width: `${ghost.width}%`,
                                  top,
                                  height: SESSION_HEIGHT,
                                  border: isSelected
                                    ? `1.5px solid ${colour}`
                                    : `1px dashed ${colour}${isHovered ? 'cc' : '80'}`,
                                  borderRadius: 6,
                                  background: isNoShow
                                    ? `repeating-linear-gradient(135deg, #d63b3b22 0 6px, #d63b3b10 6px 12px)`
                                    : isHovered
                                      ? `${colour}28`
                                      : `${colour}18`,
                                  cursor: 'pointer',
                                  display: 'flex',
                                  alignItems: 'center',
                                  paddingLeft: 6,
                                  fontSize: '0.75rem',
                                  fontWeight: 600,
                                  color: isNoShow ? '#a02525' : `${colour}c0`,
                                  opacity: isAttended ? 0.45 : 1,
                                  transform: isHovered || isSelected ? 'translateY(-1px)' : 'none',
                                  boxShadow: isSelected
                                    ? `0 0 0 3px ${colour}33, 0 4px 10px -2px ${colour}aa`
                                    : isHovered
                                      ? `0 2px 6px -2px ${colour}88`
                                      : 'none',
                                  transition: 'transform 120ms ease, box-shadow 120ms ease, background 120ms ease, border-color 120ms ease',
                                  zIndex: isSelected ? 2 : 1,
                                }}
                              >
                                {isNoShow && (
                                  <i
                                    className="fas fa-user-slash"
                                    aria-hidden
                                    style={{ marginRight: 6, fontSize: '0.78rem' }}
                                  />
                                )}
                                {initials}
                                {showQuickActions && (
                                  <button
                                    type="button"
                                    className="wsg-attendance-quickmenu-trigger"
                                    aria-label="Quick options"
                                    title="Quick options"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      if (isMenuOpen) {
                                        closeQuickMenu();
                                      } else {
                                        setMenuNoShowReason(DEFAULT_INLINE_REASON);
                                        setMenuNoShowFreeText('');
                                        setMenuAnchorRect(
                                          (e.currentTarget as HTMLElement).getBoundingClientRect(),
                                        );
                                        setMenuTarget({ session: s, dateStr });
                                        setOpenMenuKey(ghostKey);
                                      }
                                    }}
                                    style={{
                                      position: 'absolute',
                                      right: 4,
                                      top: '50%',
                                      transform: 'translateY(-50%)',
                                      width: 22,
                                      height: 22,
                                      borderRadius: '50%',
                                      border: 'none',
                                      background: isMenuOpen ? `${colour}` : `${colour}aa`,
                                      color: '#fff',
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                      cursor: 'pointer',
                                      boxShadow: '0 1px 3px rgba(0,0,0,0.18)',
                                      padding: 0,
                                      transition: 'background 120ms ease',
                                    }}
                                  >
                                    <i className="fas fa-ellipsis-h" style={{ fontSize: '0.75rem' }} />
                                  </button>
                                )}
                              </div>
                              {/* Attendance overlay — solid */}
                              {att && isAttended && att.actualArrival && att.actualDeparture && (
                                <div
                                  onClick={() => handleSessionClick(s, dateStr)}
                                  title={`${initials} · attended ${att.actualArrival}–${att.actualDeparture}${
                                    variance ? ` · ${variance}` : ''
                                  }`}
                                  style={{
                                    position: 'absolute',
                                    ...(() => {
                                      const p = barPositionPercent(att.actualArrival, att.actualDeparture);
                                      return { left: `${p.left}%`, width: `${p.width}%` };
                                    })(),
                                    top,
                                    height: SESSION_HEIGHT,
                                    border: `${isSelected ? 2 : 1.5}px solid ${colour}`,
                                    borderRadius: 6,
                                    background: `linear-gradient(180deg, ${colour}cc 0%, ${colour}99 100%)`,
                                    cursor: 'pointer',
                                    display: 'flex',
                                    alignItems: 'center',
                                    paddingLeft: 6,
                                    fontSize: '0.75rem',
                                    fontWeight: 700,
                                    color: '#fff',
                                    textShadow: '0 1px 1px rgba(0,0,0,0.2)',
                                    boxShadow: isSelected
                                      ? `0 0 0 3px ${colour}33, 0 4px 10px -2px ${colour}aa`
                                      : 'none',
                                    transform: isSelected ? 'translateY(-1px)' : 'none',
                                    zIndex: isSelected ? 3 : 2,
                                    transition: 'transform 120ms ease, box-shadow 120ms ease, border-width 120ms ease',
                                  }}
                                >
                                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                                    <span
                                      aria-hidden
                                      style={{
                                        width: 18,
                                        height: 18,
                                        borderRadius: '50%',
                                        background: '#fff',
                                        color: '#5a9a30',
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        fontSize: '0.72rem',
                                        fontWeight: 800,
                                        boxShadow:
                                          '0 0 0 1.5px #7cb342, 0 1px 2px rgba(0,0,0,0.18)',
                                      }}
                                    >
                                      <i className="fas fa-check" />
                                    </span>
                                    {initials}
                                  </span>
                                </div>
                              )}
                            </React.Fragment>
                          );
                        })}
                      </div>
                    </div>
                );
              })}

              {timeBar('bottom')}
            </div>
          </div>
        </div>
      </div>

      {/* Detail panel — slides in/out */}
      <div
        style={{
          width: panelOpen ? DETAIL_PANEL_WIDTH : 0,
          minWidth: panelOpen ? DETAIL_PANEL_WIDTH : 0,
          borderLeft: panelOpen ? '1px solid rgba(38, 146, 63, 0.22)' : 'none',
          overflow: 'hidden',
          transition: 'width 0.2s ease, min-width 0.2s ease',
        }}
      >
        {panelOpen && panelContext && (
          <AttendanceDetailPanel
            context={panelContext}
            existing={existingForPanel}
            childProfile={
              childProfiles.find(c => c.id === panelContext.childId) ?? null
            }
            isFresh={isPanelFresh}
            onUpdate={handleAttendanceLiveUpdate}
            onSave={handlePanelSave}
            onDeleted={handlePanelDeleted}
            onClose={handlePanelClose}
            confirmAction={confirmAction}
          />
        )}
      </div>

      {/* Portal'd quick-action dropdown — sits above all bars and rows. */}
      {openMenuKey && menuAnchorRect && menuTarget && createPortal(
        <div
          role="menu"
          className="wsg-attendance-quickmenu-popup"
          onClick={(e) => e.stopPropagation()}
          onMouseDown={(e) => e.stopPropagation()}
          style={{
            position: 'fixed',
            // Right-align under the trigger; clamp 8px from the viewport edge.
            top: menuAnchorRect.bottom + 6,
            left: Math.max(8, menuAnchorRect.right - 220),
            width: 220,
            padding: 10,
            borderRadius: 10,
            background:
              'radial-gradient(circle at 100% 0%, rgba(38,146,63,0.08) 0%, transparent 50%), linear-gradient(180deg, #faf8f1 0%, #ece8d9 100%)',
            border: '1px solid rgba(38,146,63,0.22)',
            boxShadow:
              '0 2px 4px rgba(13,77,26,0.06), 0 14px 32px -14px rgba(13,77,26,0.4)',
            color: '#222',
            fontWeight: 400,
            textShadow: 'none',
            textDecoration: 'none',
            zIndex: 9999,
            display: 'flex',
            flexDirection: 'column',
            gap: 8,
          }}
        >
          <button
            type="button"
            onClick={() => handleMenuMarkAttended(menuTarget.session, menuTarget.dateStr)}
            style={{
              padding: '6px 10px',
              background: '#26923f',
              color: '#fff',
              border: 'none',
              borderRadius: 6,
              fontWeight: 600,
              fontSize: '0.8rem',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 6,
            }}
          >
            <i className="fas fa-clipboard-check" style={{ fontSize: '0.75rem' }} />
            Mark as attended
          </button>

          <div style={{ height: 1, background: 'rgba(13,77,26,0.12)', margin: '2px 0' }} />

          <div style={{ fontSize: '0.7rem', color: '#3b6c3b', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
            Mark as no-show
          </div>
          <select
            value={menuNoShowReason}
            onChange={(e) => setMenuNoShowReason(e.target.value as NoShowReasonInline)}
            style={{
              padding: '5px 8px',
              borderRadius: 5,
              border: '1px solid rgba(13,77,26,0.22)',
              fontSize: '0.8rem',
              background: '#fff',
            }}
          >
            {NO_SHOW_PRESETS_INLINE.map(label => (
              <option key={label} value={label}>{label}</option>
            ))}
          </select>
          {menuNoShowReason === 'Other' && (
            <input
              type="text"
              value={menuNoShowFreeText}
              onChange={(e) => setMenuNoShowFreeText(e.target.value)}
              placeholder="Required — describe the reason"
              maxLength={500}
              style={{
                padding: '5px 8px',
                borderRadius: 5,
                border: '1px solid rgba(13,77,26,0.22)',
                fontSize: '0.8rem',
              }}
            />
          )}
          <button
            type="button"
            disabled={menuNoShowReason === 'Other' && menuNoShowFreeText.trim().length === 0}
            onClick={() => handleMenuMarkNoShow(menuTarget.session, menuTarget.dateStr)}
            style={{
              padding: '6px 10px',
              background: '#a02525',
              color: '#fff',
              border: 'none',
              borderRadius: 6,
              fontWeight: 600,
              fontSize: '0.8rem',
              cursor: menuNoShowReason === 'Other' && menuNoShowFreeText.trim().length === 0
                ? 'not-allowed'
                : 'pointer',
              opacity: menuNoShowReason === 'Other' && menuNoShowFreeText.trim().length === 0 ? 0.55 : 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 6,
            }}
          >
            <i className="fas fa-user-slash" style={{ fontSize: '0.7rem' }} />
            Confirm no-show
          </button>
        </div>,
        document.body,
      )}

      {/* Standard confirm modal — used by panel discard prompts */}
      <ConfirmationModal
        show={confirmConfig !== null}
        title={confirmConfig?.title ?? ''}
        message={confirmConfig?.message ?? ''}
        confirmText={confirmConfig?.confirmText}
        isDeleting={confirmConfig?.isDeleting}
        onConfirm={() => {
          confirmResolveRef.current?.(true);
          confirmResolveRef.current = null;
          setConfirmConfig(null);
        }}
        onCancel={() => {
          confirmResolveRef.current?.(false);
          confirmResolveRef.current = null;
          setConfirmConfig(null);
        }}
      />

      <UnlockPromptModal
        open={unlockPromptOpen}
        intent="create-attendance"
        onUnlock={() => {
          setGridLocked(false);
          setUnlockPromptOpen(false);
        }}
        onCancel={() => setUnlockPromptOpen(false)}
      />
    </div>
  );
};

export default WeeklyAttendance;
